import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, FileStack, Info } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import {
  summarise, invoicesCsv, invoicesCsvFileName, bundleInvoices,
} from '../lib/bookkeeping'
import { todayISO } from '../lib/dates'
import { Button, Card, Select, cx } from '../ui/kit'

/**
 * Die Auswertung, die zum Steuerberater geht.
 *
 * WARUM SIE ZWEI SPALTEN HAT: „Umsatz" ist keine eindeutige Zahl. Bei der
 * Soll-Versteuerung zählt das Rechnungsdatum, bei der Ist-Versteuerung der
 * Zahlungseingang, und welche gilt, hängt am Betrieb. Eine einzige Zahl zu zeigen
 * hieße, diese Entscheidung stillschweigend zu treffen — und zwar für jemanden, der
 * sie nicht kennt. Also stehen beide da, benannt, und die Kanzlei nimmt die richtige.
 *
 * WARUM DIE ORTSTAXE GETRENNT STEHT: sie ist kein Ertrag, sondern wird für die
 * Gemeinde eingehoben. Wer sie als Umsatz meldet, versteuert fremdes Geld.
 *
 * WARUM DIE KAUTIONEN NICHT MITGERECHNET WERDEN: dasselbe in Grün. Eine Kaution ist
 * Fremdgeld, das zurückgeht — sie steht als eigene Zeile und nirgends sonst.
 */
export default function BookkeepingView() {
  const { lang, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const navigate = useNavigate()
  const de = lang === 'de'

  const thisYear = todayISO().slice(0, 4)
  const [period, setPeriod] = useState<string>(thisYear)

  const years = useMemo(() => {
    const all = new Set<string>([thisYear])
    for (const i of store.db.invoices) all.add(i.issue_date.slice(0, 4))
    return [...all].sort().reverse()
  }, [store.db.invoices, thisYear])

  const { from, to } = useMemo(() => {
    if (period.length === 7) {
      // Ein Monat: der letzte Tag ergibt sich aus dem nullten Tag des Folgemonats.
      const y = Number(period.slice(0, 4))
      const m = Number(period.slice(5, 7))
      const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
      return { from: `${period}-01`, to: last }
    }
    return { from: `${period}-01-01`, to: `${period}-12-31` }
  }, [period])

  const sum = useMemo(() => summarise({
    from, to,
    invoices: store.db.invoices,
    invoiceItems: store.db.invoice_items,
    payments: store.db.payments,
    quotes: store.db.quotes,
    bookings: store.db.bookings,
    today: todayISO(),
  }), [from, to, store.db])

  // Wie viele Belege im Bündel landen — das sind mehr als `invoiceCount`, weil
  // Gutschriften und stornierte Rechnungen dazugehören. Sie tragen eine Nummer.
  const bundleCount = useMemo(
    () => bundleInvoices(store.db.invoices, from, to).length,
    [store.db.invoices, from, to])

  const methodLabel = (m: string) => (de
    ? ({ cash: 'Bar', card: 'Karte', bank_transfer: 'Überweisung', stripe: 'Stripe',
         paypal: 'PayPal', other: 'Anders' } as Record<string, string>)[m] ?? m
    : ({ cash: 'Cash', card: 'Card', bank_transfer: 'Bank transfer', stripe: 'Stripe',
         paypal: 'PayPal', other: 'Other' } as Record<string, string>)[m] ?? m)

  const months = useMemo(() => {
    const out: string[] = []
    for (let m = 1; m <= 12; m++) out.push(`${thisYear}-${String(m).padStart(2, '0')}`)
    return out
  }, [thisYear])

  const download = () => {
    const csv = invoicesCsv({
      invoices: store.db.invoices,
      invoiceItems: store.db.invoice_items,
      payments: store.db.payments,
      bookings: store.db.bookings,
      from,
      to,
      guestName: (id) => store.guestName(id),
      guestCountry: (id) => store.guestById(id)?.country ?? '',
      unitName: (bookingId) => {
        const b = store.db.bookings.find((x) => x.id === bookingId)
        return b ? store.unitById(b.unit_id)?.name ?? '' : ''
      },
    })
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = invoicesCsvFileName(store.db.tenant.name, from, to)
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={period} onChange={(e) => setPeriod(e.target.value)}
          className="w-auto flex-1">
          {years.map((y) => (
            <option key={y} value={y}>{de ? `Jahr ${y}` : `Year ${y}`}</option>
          ))}
          {months.map((m) => (
            <option key={m} value={m}>
              {new Intl.DateTimeFormat(de ? 'de-AT' : 'en-GB',
                { month: 'long', year: 'numeric' }).format(new Date(`${m}-01T00:00:00`))}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="secondary" onClick={download}>
          <Download size={15} /> CSV
        </Button>
      </div>

      {/*
        Der Weg zu den Belegen selbst.
        Eine Liste ohne Rechnungen ist für eine Buchhaltung wertlos — die Kanzlei bucht
        gegen Belege. Der Knopf steht deshalb neben dem CSV und nicht darunter, und der
        Satz daneben sagt, was der Vermieter bekommt, statt „Belegbündel" für sich
        sprechen zu lassen.
      */}
      <Card className="p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-medium">
            {de ? 'Belege als ein PDF' : 'All documents as one PDF'}
          </div>
          <p className="text-xs text-muted mt-0.5">
            {de
              ? `Alle ${bundleCount} festgeschriebenen ${bundleCount === 1 ? 'Beleg' : 'Belege'} des Zeitraums hintereinander, mit Deckblatt und Lückenprüfung der Rechnungsnummern. Öffnen, „Als PDF sichern", an die Kanzlei schicken.`
              : `All ${bundleCount} issued ${bundleCount === 1 ? 'document' : 'documents'} of the period back to back, with a cover sheet and a check for gaps in the invoice numbers. Open it, "Save as PDF", send it to your accountant.`}
          </p>
        </div>
        <Button size="sm" variant="secondary" disabled={bundleCount === 0}
          onClick={() => navigate(`/document/bundle/${from}/${to}`)}>
          <FileStack size={15} />
          {de ? 'Öffnen' : 'Open'}
        </Button>
      </Card>

      {/* Die beiden Sichten nebeneinander, benannt. */}
      <div className="grid grid-cols-2 gap-3">
        <Figure
          label={de ? 'Abgerechnet' : 'Invoiced'}
          hint={de ? 'nach Rechnungsdatum' : 'by invoice date'}
          value={formatMoney(sum.invoicedGross)}
          note={`${sum.invoiceCount} ${de
            ? (sum.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen')
            : (sum.invoiceCount === 1 ? 'invoice' : 'invoices')}`}
        />
        <Figure
          label={de ? 'Eingegangen' : 'Received'}
          hint={de ? 'nach Zahlungseingang' : 'by payment date'}
          value={formatMoney(sum.receivedGross)}
          note={sum.receivedByMethod
            .map((r) => `${methodLabel(r.method)} ${formatMoney(r.amount)}`).join(' · ')}
        />
      </div>

      <Card className="p-4 space-y-2">
        <h3 className="font-medium text-sm">
          {de ? 'Umsatz je Steuersatz' : 'Turnover by VAT rate'}
          <span className="text-muted font-normal"> · {de ? 'nach Rechnungsdatum' : 'by invoice date'}</span>
        </h3>
        {sum.vatRows.length === 0 ? (
          <p className="text-sm text-muted">
            {de ? 'Keine festgeschriebenen Rechnungen in diesem Zeitraum.'
                : 'No issued invoices in this period.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted">
                <th className="text-left font-medium pb-1">{de ? 'Satz' : 'Rate'}</th>
                <th className="text-right font-medium pb-1">{de ? 'Netto' : 'Net'}</th>
                <th className="text-right font-medium pb-1">{de ? 'USt.' : 'VAT'}</th>
                <th className="text-right font-medium pb-1">{de ? 'Brutto' : 'Gross'}</th>
              </tr>
            </thead>
            <tbody>
              {sum.vatRows.map((r) => (
                <tr key={r.rate} className="border-t border-border">
                  <td className="py-1.5">{r.rate} %</td>
                  <td className="py-1.5 text-right tabular-nums">{formatMoney(r.net)}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatMoney(r.vat)}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatMoney(r.gross)}</td>
                </tr>
              ))}
              <tr className="border-t border-border font-medium">
                <td className="py-1.5">{de ? 'Summe' : 'Total'}</td>
                <td className="py-1.5 text-right tabular-nums">{formatMoney(sum.invoicedNet)}</td>
                <td className="py-1.5 text-right tabular-nums">{formatMoney(sum.invoicedVat)}</td>
                <td className="py-1.5 text-right tabular-nums">{formatMoney(sum.invoicedGross)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-4 space-y-1.5 text-sm">
        <Row label={de ? 'Offene Forderungen zum Stichtag' : 'Outstanding at the cut-off'}
          value={formatMoney(sum.openInvoiced)}
          note={`${sum.openInvoiceCount} ${de
            ? (sum.openInvoiceCount === 1 ? 'Rechnung' : 'Rechnungen')
            : (sum.openInvoiceCount === 1 ? 'invoice' : 'invoices')}`}
          tone={sum.openInvoiced > 0 ? 'warn' : undefined} />
        <Row label={de ? 'davon Anzahlungen eingegangen' : 'of which deposits received'}
          value={formatMoney(sum.prepaymentsReceived)} />
        <Row label={de ? 'Gutschriften (Storno)' : 'Credit notes'}
          value={formatMoney(sum.creditedGross)}
          note={`${sum.creditCount}`} />
        <Row label={de ? 'Offene Angebote' : 'Open quotes'}
          value={formatMoney(sum.quotedOpenGross)}
          note={`${sum.quotedOpenCount}`} />
        <div className="pt-2 mt-1 border-t border-border space-y-1.5">
          <Row label={de ? 'Ortstaxe (durchlaufender Posten)' : 'Tourist tax (pass-through)'}
            value={formatMoney(sum.touristTax)} />
          <Row label={de ? 'Kautionen einbehalten (Fremdgeld)' : 'Deposits held (third-party money)'}
            value={formatMoney(sum.depositsHeld)} />
        </div>
      </Card>

      <div className="flex items-start gap-2 text-xs text-muted">
        <Info size={14} className="shrink-0 mt-0.5" />
        <p>
          {de
            ? `Zeitraum ${formatDate(from)} – ${formatDate(to)}. Gezählt werden nur festgeschriebene Rechnungen; Entwürfe bleiben außen vor. Ortstaxe und Kautionen sind kein Umsatz und stehen deshalb getrennt. Welche der beiden Sichten für Ihren Betrieb gilt — Soll- oder Ist-Versteuerung — weiß Ihre Kanzlei. Die CSV führt Netto und Umsatzsteuer je Steuersatz in eigenen Spalten, dazu Leistungszeitraum und Storno-Bezug; Buchungssätze mit Konto und Gegenkonto stehen bewusst nicht darin — den Kontenrahmen kennt nur Ihre Kanzlei.`
            : `Period ${formatDate(from)} – ${formatDate(to)}. Only issued invoices are counted; drafts are left out. Tourist tax and deposits are not turnover and are listed separately. Which of the two views applies to your business is a question for your accountant. The CSV lists net and VAT per rate in their own columns, plus the period of supply and the cancellation reference; it deliberately contains no ledger accounts — only your accountant knows the chart of accounts.`}
        </p>
      </div>
    </div>
  )
}

function Figure({ label, hint, value, note }: {
  label: string; hint: string; value: string; note?: string
}) {
  return (
    <Card className="p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-[11px] text-muted opacity-80">{hint}</div>
      <div className="text-lg font-semibold tabular-nums mt-1">{value}</div>
      {note && <div className="text-[11px] text-muted mt-0.5 truncate">{note}</div>}
    </Card>
  )
}

function Row({ label, value, note, tone }: {
  label: string; value: string; note?: string; tone?: 'warn'
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-muted flex-1 min-w-0">{label}</span>
      {note && <span className="text-xs text-muted">{note}</span>}
      <span className={cx('tabular-nums font-medium', tone === 'warn' && 'text-warn')}>
        {value}
      </span>
    </div>
  )
}
